import { useCallback, useMemo, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { AdminUserStatisticsResponse } from '@wlb/shared/api/admin/adminTypes';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../../components/ui/Avatar';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { TextField } from '../../components/ui/TextField';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { formatCount, formatDate } from '../../utils/format';
import { resolvePictureUrl } from '../../utils/resolveImageUrl';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

const PAGE_SIZE = 20;

export function UserStatistics() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const debouncedSearch = useDebounce(search);

  const load = useCallback(
    () =>
      adminService.getUserStatistics({
        userName: debouncedSearch.trim() || null,
        pageNumber,
        pageSize: PAGE_SIZE,
        timezoneOffset: new Date().getTimezoneOffset(),
      }),
    [debouncedSearch, pageNumber],
  );

  const requestKey = `${debouncedSearch}|${pageNumber}`;

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    true,
    'userStatistics',
  );

  const columns = useMemo<readonly DataTableColumn<AdminUserStatisticsResponse>[]>(
    () => [
      {
        key: 'user',
        header: t('users.columns.user'),
        render: row => {
          const name = row.userName || t('common.none');
          return (
            <div className="ui-table__cell-with-avatar">
              <Avatar src={resolvePictureUrl(row.picture)} name={name} />
              <span className="ui-table__primary">{name}</span>
            </div>
          );
        },
      },
      {
        key: 'joined',
        header: t('users.columns.joined'),
        render: row => formatDate(row.joiningDate) || t('common.none'),
      },
      {
        key: 'rank',
        header: t('users.columns.rank'),
        numeric: true,
        render: row => formatCount(row.userRank),
      },
      {
        key: 'points',
        header: t('users.columns.points'),
        numeric: true,
        render: row => formatCount(row.totalPoints),
      },
      {
        key: 'posts',
        header: t('users.columns.posts'),
        numeric: true,
        render: row => formatCount(row.posts),
      },
      {
        key: 'comments',
        header: t('users.columns.comments'),
        numeric: true,
        render: row => formatCount(row.comments),
      },
      {
        key: 'likes',
        header: t('users.columns.likes'),
        numeric: true,
        render: row => formatCount(row.likes),
      },
      {
        key: 'shares',
        header: t('users.columns.shares'),
        numeric: true,
        render: row => formatCount(row.shares),
      },
      {
        key: 'logins',
        header: t('users.columns.logins'),
        numeric: true,
        render: row => formatCount(row.logins),
      },
    ],
    [t],
  );

  const totalCount = data?.totalCount ?? 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  return (
    <>
      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('users.searchPlaceholder')}
            value={search}
            onChange={event => {
              setPageNumber(1);
              setSearch(event.target.value);
            }}
          />
        </div>
      </section>

      <DataTable
        caption={t('statistics.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={row => row.userId}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={reload}
        emptyTitle={t('users.emptyTitle')}
        emptyMessage={t('users.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={pageNumber}
              totalPages={totalPages}
              totalCount={totalCount}
              hasPreviousPage={pageNumber > 1}
              hasNextPage={pageNumber < totalPages}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />
    </>
  );
}
